import { useEffect } from 'react';

/**
 * GlowCursor — RAF + lerp smooth glowing orb.
 *
 * Key fixes:
 *  1. No initialized.current guard — breaks React StrictMode (double-invoke kills cursor)
 *  2. No mixBlendMode: screen — invisible on pure black bg
 *  3. Elements appended to document.body so fixed positioning always works
 *  4. Uses left/top CSS (not transform) so no overflow:hidden clipping
 *  5. Cursor is hidden via a <style> tag so it applies everywhere including iframes
 */
const GlowCursor = () => {
    useEffect(() => {
        // Hide native cursor globally
        const style = document.createElement('style');
        style.id = 'glow-cursor-hide';
        style.textContent = '*, *::before, *::after { cursor: none !important; }';
        document.head.appendChild(style);

        const lerp = (a, b, t) => a + (b - a) * t;

        // ── Outer halo ────────────────────────────────────────────────────
        const halo = document.createElement('div');
        Object.assign(halo.style, {
            position: 'fixed',
            top: '-100px',
            left: '-100px',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            pointerEvents: 'none',
            zIndex: '2147483647',
            background: 'radial-gradient(circle, rgba(168,130,255,0.55) 0%, rgba(139,92,246,0.25) 45%, transparent 100%)',
            boxShadow: '0 0 20px 8px rgba(139,92,246,0.35), 0 0 50px 16px rgba(139,92,246,0.12)',
            transform: 'translate(-50%, -50%)',
            willChange: 'top, left',
        });

        // ── Inner bright dot ──────────────────────────────────────────────
        const dot = document.createElement('div');
        Object.assign(dot.style, {
            position: 'fixed',
            top: '-100px',
            left: '-100px',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            pointerEvents: 'none',
            zIndex: '2147483647',
            background: 'radial-gradient(circle, #ffffff 0%, #ddd6fe 50%, #8B5CF6 100%)',
            boxShadow: '0 0 12px 5px rgba(139,92,246,0.95), 0 0 28px 10px rgba(139,92,246,0.5), 0 0 0 1px rgba(255,255,255,0.6)',
            transform: 'translate(-50%, -50%)',
            willChange: 'top, left',
        });

        // Mount at body level — bypasses all React stacking contexts
        document.body.appendChild(halo);
        document.body.appendChild(dot);

        const mouse = { x: -200, y: -200 };
        const dotPos = { x: -200, y: -200 };
        const haloPos = { x: -200, y: -200 };
        let raf;
        let hasMovedOnce = false;

        const onMove = (e) => {
            mouse.x = e.clientX;
            mouse.y = e.clientY;
            hasMovedOnce = true;
        };

        const tick = () => {
            if (hasMovedOnce) {
                dotPos.x = lerp(dotPos.x, mouse.x, 0.45);
                dotPos.y = lerp(dotPos.y, mouse.y, 0.45);
                haloPos.x = lerp(haloPos.x, mouse.x, 0.11);
                haloPos.y = lerp(haloPos.y, mouse.y, 0.11);

                dot.style.left = `${dotPos.x}px`;
                dot.style.top = `${dotPos.y}px`;
                halo.style.left = `${haloPos.x}px`;
                halo.style.top = `${haloPos.y}px`;
            }
            raf = requestAnimationFrame(tick);
        };

        window.addEventListener('mousemove', onMove, { passive: true });
        raf = requestAnimationFrame(tick);

        return () => {
            window.removeEventListener('mousemove', onMove);
            cancelAnimationFrame(raf);
            halo.remove();
            dot.remove();
            style.remove();
        };
    }, []);

    return null;
};

export default GlowCursor;
